import { useEffect, useState } from "react";
import { BarChart3, CalendarDays, Lock, MousePointerClick, TrendingUp } from "lucide-react";

const LABELS = {
  header: "Botão do topo",
  hero: "Hero — Agendar sessão",
  identificacao: "Identificação — Converse comigo",
  faq: "FAQ — Me chame no WhatsApp",
  manifesto: "Capítulos — Entenda a abordagem",
  pilares: "Pilares — Entenda a abordagem",
  final: "CTA final — Converse comigo",
  rodape: "Rodapé — telefone",
  flutuante: "Botão flutuante do WhatsApp",
};

export default function Stats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [denied, setDenied] = useState(false);
  const [key, setKey] = useState(() => sessionStorage.getItem("statsKey") || "");
  const [input, setInput] = useState("");

  useEffect(() => {
    if (!key) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/click-stats`, {
      headers: { "X-Stats-Key": key },
    })
      .then((r) => {
        if (r.status === 401) {
          sessionStorage.removeItem("statsKey");
          setDenied(true);
          setKey("");
          return null;
        }
        return r.json();
      })
      .then((d) => d && setData(d))
      .catch(() => setError(true));
  }, [key]);

  const submitKey = (e) => {
    e.preventDefault();
    setDenied(false);
    sessionStorage.setItem("statsKey", input);
    setKey(input);
  };

  if (!key && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand px-6">
        <form
          onSubmit={submitKey}
          data-testid="stats-login-form"
          className="w-full max-w-sm rounded-2xl bg-white p-10 shadow-[0_16px_48px_rgba(45,51,47,0.1)]"
        >
          <Lock className="h-8 w-8 text-copper" strokeWidth={1.6} />
          <h1 className="mt-5 font-serif text-3xl font-medium text-bark">
            Painel restrito
          </h1>
          <p className="mt-2 text-sm text-moss">
            Digite a senha para ver as estatísticas.
          </p>
          <input
            data-testid="stats-password-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Senha"
            className="mt-6 w-full rounded-xl border border-bark/15 bg-sand px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-brand"
            autoFocus
          />
          {denied && (
            <p data-testid="stats-denied" className="mt-3 text-sm text-red-700">
              Senha incorreta. Tente novamente.
            </p>
          )}
          <button
            data-testid="stats-login-button"
            type="submit"
            className="mt-6 w-full rounded-full bg-brand py-3.5 text-sm font-semibold text-sand transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-deep"
          >
            Acessar painel
          </button>
        </form>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const todayCount =
    data?.by_day?.find((d) => d.date === today)?.count ?? 0;
  const last7 =
    data?.by_day?.slice(-7).reduce((sum, d) => sum + d.count, 0) ?? 0;

  return (
    <div className="min-h-screen bg-sand px-6 py-16">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-copper">
          Painel interno
        </p>
        <h1 className="mt-4 font-serif text-4xl font-medium text-bark sm:text-5xl">
          Cliques no WhatsApp
        </h1>
        <p className="mt-4 text-base text-moss">
          Quantos visitantes clicaram para conversar com você.
        </p>

        {error && (
          <p data-testid="stats-error" className="mt-10 text-sm text-red-700">
            Não foi possível carregar as estatísticas agora.
          </p>
        )}

        {data && (
          <>
            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div data-testid="stats-total" className="rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <MousePointerClick className="h-6 w-6 text-copper" strokeWidth={1.6} />
                <p className="mt-4 font-serif text-5xl font-medium text-brand">{data.total}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-moss">
                  cliques no total
                </p>
              </div>
              <div data-testid="stats-today" className="rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <CalendarDays className="h-6 w-6 text-copper" strokeWidth={1.6} />
                <p className="mt-4 font-serif text-5xl font-medium text-brand">{todayCount}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-moss">
                  hoje
                </p>
              </div>
              <div data-testid="stats-last7" className="rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <TrendingUp className="h-6 w-6 text-copper" strokeWidth={1.6} />
                <p className="mt-4 font-serif text-5xl font-medium text-brand">{last7}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-moss">
                  últimos 7 dias
                </p>
              </div>
            </div>

            <div className="mt-10 rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-medium text-bark">
                <BarChart3 className="h-5 w-5 text-copper" strokeWidth={1.6} />
                De onde vêm os cliques
              </h2>
              <ul className="mt-6 space-y-3" data-testid="stats-by-location">
                {data.by_location.length === 0 && (
                  <li className="text-sm text-moss">Nenhum clique registrado ainda.</li>
                )}
                {data.by_location.map((item) => (
                  <li key={item.location} className="flex items-center justify-between border-b border-bark/8 pb-3 text-sm">
                    <span className="font-medium text-bark">
                      {LABELS[item.location] || item.location}
                    </span>
                    <span className="font-semibold text-brand">{item.count}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <h2 className="font-serif text-2xl font-medium text-bark">
                Últimos 30 dias
              </h2>
              <ul className="mt-6 space-y-2" data-testid="stats-by-day">
                {data.by_day.length === 0 && (
                  <li className="text-sm text-moss">Nenhum clique registrado ainda.</li>
                )}
                {[...data.by_day].reverse().map((d) => (
                  <li key={d.date} className="flex items-center justify-between text-sm">
                    <span className="text-moss">
                      {d.date.split("-").reverse().join("/")}
                    </span>
                    <span className="font-semibold text-bark">{d.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
